// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { FileContext } from '@/file/api/types';
import { z } from 'zod';

const uploadFileResponseSchema = z
  .object({
    data: z.object({ fileId: z.string(), fileKey: z.string() }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, ...data }));

type UploadFileResponse = z.infer<typeof uploadFileResponseSchema>;

const DEFAULT_FILE_CONTEXT: FileContext = 'MISC';

interface UploadFileOptions {
  context?: FileContext;
  isPublic?: boolean;
  entityId?: string;
}

/**
 * Uploads a file to the remote API.
 *
 * @param file - The file to upload.
 * @param options.context - The context of the file. Defaults to 'MISC'.
 * @param options.isPublic - Whether the file should be publicly accessible.
 * Defaults to false.
 * @param options.entityId - The ID of the entity to which the file belongs.
 * This should be provided when uploading a file for an entity.
 */
export async function uploadFile(
  file: File,
  options?: UploadFileOptions
): Promise<UploadFileResponse> {
  const url = '/v1/file';
  const context = options?.context ?? DEFAULT_FILE_CONTEXT;
  const isPublic = options?.isPublic ?? false;
  const formData = new FormData();

  formData.append('fileType', context);
  formData.append('isPublic', isPublic.toString());

  if (options?.entityId) {
    formData.append('forEntity', options.entityId);
  }

  formData.append('data', file);

  const response = await api.post(url, formData);

  return parseResponse(uploadFileResponseSchema, response.data, {
    schemaName: 'UploadFile',
    endpoint: url,
  });
}
